/**
 * RotationHistoryList — the queue's per-run history: "Last checked" stamp on
 * top (the optimizer is alive even when nothing rotated), then one expandable
 * row per recorded run, newest first. One row open at a time.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useState } from 'react';
import type { RotationHistoryResult } from '@trafficpulse/shared';

import { Button, LoadingSpinner } from '@/components/ui';
import { formatDateTimeGb } from '@/lib/utils';

import RotationHistoryRow from './RotationHistoryRow';
import { filterRunsBySearch } from './history-search';

export interface RotationHistoryListProps {
  history?: RotationHistoryResult;
  isLoading: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /** Keeps runs that touched a matching element. */
  search?: string;
  onClearSearch?: () => void;
}

const RotationHistoryList: FC<RotationHistoryListProps> = ({
  history,
  isLoading,
  isError,
  onRetry,
  search = '',
  onClearSearch,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3">
        <p className="text-xs text-destructive">Could not load the rotation history.</p>
        {onRetry ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        ) : null}
      </div>
    );
  }
  const all = history?.runs ?? [];
  const runs = filterRunsBySearch(all, search);
  const term = search.trim();
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-2">
        {history?.lastCheckedAt ? (
          <p className="text-[10.5px] text-muted-foreground">
            Last checked: {formatDateTimeGb(history.lastCheckedAt)}
          </p>
        ) : null}
        {term && runs.length > 0 ? (
          <p className="text-[10.5px] text-muted-foreground">
            · showing{' '}
            <span className="font-medium text-foreground">{runs.length}</span> of{' '}
            {all.length} runs
          </p>
        ) : null}
      </div>
      {runs.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
          {term ? (
            <>
              <p className="text-xs text-muted-foreground">
                No run touched anything matching{' '}
                <span className="font-medium text-foreground">“{term}”</span>.
              </p>
              {onClearSearch ? (
                <button
                  type="button"
                  onClick={onClearSearch}
                  className="mt-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
                >
                  Clear the search
                </button>
              ) : null}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              No applied rotations yet — each run that changes the flow is
              recorded here (the most recent 20) with its full before → after
              story.
            </p>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-border/60 rounded-md border border-border/60 bg-card/40 px-2">
          {runs.map((run) => (
            <RotationHistoryRow
              key={run.id}
              run={run}
              expanded={expandedId === run.id}
              onToggle={() =>
                setExpandedId((cur) => (cur === run.id ? null : run.id))
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default RotationHistoryList;
